import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Textarea } from '../ui.jsx';
import { ContentStudio as ContentStudioEmbedded } from './ContentStudio.jsx';

const PLATFORMS = ['google_lsa','google_ads','meta_ads','tiktok_ads','youtube','email','sms','gbp','offline','referral'];
const PLATFORM_LABEL = { google_lsa:'Google LSA', google_ads:'Google Ads', google_search:'Google Search', meta:'Meta Ads', meta_ads:'Meta Ads', tiktok:'TikTok', tiktok_ads:'TikTok Ads', youtube:'YouTube', email:'Email', sms:'SMS', gbp:'Google Business', offline:'Offline / Direct Mail', referral:'Referral' };
const PLATFORM_COLOR = { google_lsa:'#4285f4', google_ads:'#34a853', google_search:'#34a853', meta:'#1877f2', meta_ads:'#1877f2', tiktok:'#000', tiktok_ads:'#000', youtube:'#ff0000', email:G.green, sms:G.purple, gbp:G.orange, offline:G.textMuted, referral:G.cyan };

export function Marketing({ toast, initialTab }) {
  // Default tab is Content Studio. Pass `initialTab="campaigns"` from the
  // Overview-group "Marketing Overview" entry to land on the dashboard view.
  const [tab, setTab] = useState(initialTab || 'content');
  const [data, setData] = useState({ summary:{}, campaigns:[], sources:[], pendingAssets:[] });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => api('/api/marketing/dashboard').then(setData).catch(e=>toast(e.message,'error')).finally(()=>setLoading(false));
  useEffect(() => { load(); }, []);

  const [previewing, setPreviewing] = useState(null);
  const [socialPosting, setSocialPosting] = useState(null);

  const removeC = async c => {
    if (!confirm(`Delete "${c.name}"?`)) return;
    try { await api(`/api/marketing/campaigns/${c.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch(e) { toast(e.message,'error'); }
  };
  const decide = async (a, action) => {
    try { await api(`/api/marketing/assets/${a.id}/${action}`, { method:'POST' }); load(); toast(action); }
    catch(e) { toast(e.message,'error'); }
  };
  const generate = async (campaignId, kind = 'mixed') => {
    const hook = prompt('Optional angle for the AI (e.g. "summer heat wave", "estimate season"). Leave blank for general.', '');
    if (hook === null) return;
    const label = kind === 'video' ? '🎬 Generating video ads... takes 1-3 minutes' : '🤖 Generating creatives... takes ~5 seconds';
    toast(label);
    try {
      const r = await api(`/api/marketing/campaigns/${campaignId}/generate-creatives`, { method:'POST', body:{ count: kind==='video'?2:3, hook, kind }});
      if (r.videoErrors?.length) toast(`⚠️ Some videos failed: ${r.videoErrors[0]}`, 'error');
      await load();
      toast(`✅ Generated ${r.count} drafts — opening the first one to review`);
      // Auto-open the first new creative so user sees the result immediately
      if (r.creatives?.length) {
        const first = r.creatives[0];
        setPreviewing({
          id: first.id, type: first.type, status:'pending',
          title: first.title || first.headline,
          copy: first.copy,
          imageUrl: first.image_url,
          aiGenerated: true,
          campaignName: '', platform: '',
        });
      }
      // Scroll the page to the top where the pending queue lives
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch {}
    } catch(e) { toast(e.message,'error'); }
  };

  if (loading) return <Spinner sz={32}/>;

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Marketing</h1>
      <Btn v="primary" onClick={()=>setEditing({ platform:'google_lsa', status:'active' })}>+ New Campaign</Btn>
    </div>

    {/* Tab bar */}
    <div style={{ display:'flex', gap:4, marginBottom:18, borderBottom:`1px solid ${G.cardBorder}`, overflowX:'auto' }}>
      {[
        { k:'content',     label:'✍️ Content Studio' },
        { k:'campaigns',   label:'📣 Campaigns' },
        { k:'reports',     label:'📈 Reports' },
        { k:'connections', label:'🔌 Connections' },
      ].map(t => (
        <button key={t.k} onClick={()=>setTab(t.k)}
          style={{ background:'none', border:'none', color: tab===t.k?G.purple:G.textMuted,
            padding:'10px 16px', fontWeight: tab===t.k?700:500, cursor:'pointer', whiteSpace:'nowrap',
            borderBottom: tab===t.k?`2px solid ${G.purple}`:'2px solid transparent', marginBottom:-1 }}>
          {t.label}
        </button>
      ))}
    </div>

    {tab === 'content' && <ContentStudioEmbedded toast={toast}/>}
    {tab === 'reports' && <ReportsTab toast={toast}/>}
    {tab === 'connections' && <ConnectionsTab toast={toast}/>}
    {tab !== 'campaigns' ? null : <>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:18 }}>
      <Stat label="Total Leads" value={data.summary.totalLeads} sub={`${data.summary.bookRate}% book rate`} color={G.cyan}/>
      <Stat label="Total Spend" value={fmtUsd(data.summary.totalSpend)} sub={`${data.summary.activeCampaigns} active`} color={G.orange}/>
      <Stat label="Attributed Revenue" value={fmtUsd(data.summary.totalRevenue)} sub="from campaigns" color={G.green}/>
      <Stat label="Blended ROAS" value={`${data.summary.blendedRoas}×`} sub={`Avg CPL ${fmtUsd(data.summary.blendedCpl)}`} color={G.purple}/>
    </div>

    {data.pendingAssets.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:14, borderColor:G.yellow+'66' }}>
      <strong style={{ color:G.yellow }}>⚠️ {data.pendingAssets.length} creative{data.pendingAssets.length>1?'s':''} awaiting your approval</strong>
      <div style={{ marginTop:10, display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
        {data.pendingAssets.map(a => (
          <div key={a.id} style={{ ...styles.card, padding:12 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
              <Badge c={PLATFORM_COLOR[a.platform]||G.purple} bg={(PLATFORM_COLOR[a.platform]||G.purple)+'22'}>{a.type}</Badge>
              <span style={{ fontSize:11, color:G.textMuted }}>{a.campaignName}</span>
              {a.aiGenerated && <Badge c={G.purple} bg={G.purpleDim}>🤖 AI</Badge>}
            </div>
            <strong style={{ fontSize:14 }}>{a.title}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4, lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{a.copy}</div>
            <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
              <Btn sz="sm" v="cyan" onClick={()=>setPreviewing(a)}>👁️ Preview</Btn>
              <Btn sz="sm" v="purple" onClick={()=>setSocialPosting(a)}>📲 Post</Btn>
              <Btn sz="sm" v="green" onClick={()=>decide(a,'approve')}>✓ Approve</Btn>
              <Btn sz="sm" v="danger" onClick={()=>decide(a,'reject')}>✗</Btn>
            </div>
          </div>
        ))}
      </div>
    </div>}

    <PreviewModal asset={previewing} onClose={()=>setPreviewing(null)} onSaved={()=>{ load(); setPreviewing(null); toast('Saved'); }} onApprove={async ()=>{ if (previewing) { await decide(previewing,'approve'); setPreviewing(null); }}} toast={toast}/>
    <SocialPostModal asset={socialPosting} onClose={()=>setSocialPosting(null)} onDone={()=>{ setSocialPosting(null); load(); }} toast={toast}/>

    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Campaigns</h3>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr>{['Name','Platform','Status','Spend','Leads','Booked','Revenue','CPL','ROAS',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
          <tbody>{data.campaigns.map(c => (
            <tr key={c.id} onClick={()=>setEditing(c)} style={{ cursor:'pointer' }}>
              <td style={{ ...styles.td, fontWeight:600 }}>{c.name}</td>
              <td style={styles.td}><Badge c={PLATFORM_COLOR[c.platform]||G.purple} bg={(PLATFORM_COLOR[c.platform]||G.purple)+'22'}>{PLATFORM_LABEL[c.platform]||c.platform}</Badge></td>
              <td style={styles.td}><Badge c={c.status==='active'?G.green:G.textMuted} bg={(c.status==='active'?G.green:G.textMuted)+'22'}>{c.status}</Badge></td>
              <td style={styles.td}>{fmtUsd(c.spendToDate)}</td>
              <td style={styles.td}>{c.leads}</td>
              <td style={styles.td}>{c.bookedJobs} ({c.bookRate}%)</td>
              <td style={styles.td}>{fmtUsd(c.revenueAttributed)}</td>
              <td style={styles.td}>{fmtUsd(c.cpl)}</td>
              <td style={{ ...styles.td, fontWeight:700, color: c.roas>=2?G.green:c.roas>=1?G.yellow:G.red }}>{c.roas}×</td>
              <td style={styles.td} onClick={e=>e.stopPropagation()}>
                <div style={{ display:'flex', gap:4 }}>
                  <Btn sz="xs" v="cyan" onClick={()=>generate(c.id, 'mixed')}>🤖 Mix</Btn>
                  <Btn sz="xs" v="purple" onClick={()=>generate(c.id, 'video')} title="Generate video ads only">🎬 Video</Btn>
                  <Btn sz="xs" v="danger" onClick={()=>removeC(c)}>×</Btn>
                </div>
              </td>
            </tr>
          ))}</tbody>
        </table>
        {data.campaigns.length === 0 && <div style={{ padding:24, textAlign:'center', color:G.textMuted }}>No campaigns yet.</div>}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Lead Sources (organic + paid)</h3>
        {data.sources.map(s => (
          <div key={s.source} style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4, fontSize:13 }}>
              <strong>{s.source}</strong>
              <span style={{ color:G.textMuted }}>{s.leads} leads · {s.bookRate}% booked</span>
            </div>
            <div style={{ height:6, background:G.input, borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:`${Math.min(100, s.leads*4)}%`, height:'100%', background:G.purple }}/>
            </div>
            {s.pipelineValue > 0 && <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{fmtUsd(s.pipelineValue)} pipeline value</div>}
          </div>
        ))}
        {data.sources.length === 0 && <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>No leads yet.</div>}
      </div>
    </div>
    </>}

    <CampaignModal c={editing} onClose={()=>setEditing(null)} onSaved={()=>{ load(); setEditing(null); toast('Saved'); }}/>
  </div>;
}

// ─── Reports tab: unified spend/leads/CPA by platform + by campaign ──────────
function ReportsTab({ toast }) {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => api('/api/marketing/reports').then(setData).catch(e=>toast(e.message,'error'));
  useEffect(() => { load(); }, []);
  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api('/api/marketing/sync-all', { method:'POST' });
      const ok = Object.values(r.results||{}).filter(x=>x?.ok).length;
      toast(`✅ Synced ${ok} platform${ok===1?'':'s'}`);
      await load();
    } catch(e) { toast(e.message,'error'); } finally { setSyncing(false); }
  };
  if (!data) return <Spinner sz={32}/>;

  const t = data.totals || {};
  return <div>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
      <div style={{ color:G.textMuted, fontSize:13 }}>Rolling last 30 days · auto-synced from connected ad accounts</div>
      <Btn v="primary" onClick={syncAll} disabled={syncing}>{syncing?<Spinner sz={14}/>:'🔄 Sync now'}</Btn>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:18 }}>
      <Stat label="Total Spend" value={fmtUsd(t.spend)} sub={`${t.leads} leads`} color={G.orange}/>
      <Stat label="Cost / Lead" value={fmtUsd(t.cpl)} sub={`${t.bookRate}% book rate`} color={G.cyan}/>
      <Stat label="Cost / Booked Job" value={fmtUsd(t.cpa)} sub={`${t.booked} booked`} color={G.purple}/>
      <Stat label="Revenue" value={fmtUsd(t.revenue)} sub="attributed" color={G.green}/>
      <Stat label="ROAS" value={`${t.roas||0}×`} sub={t.roas>=2?'healthy':t.roas>=1?'breakeven':'losing'} color={t.roas>=2?G.green:t.roas>=1?G.yellow:G.red}/>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>By Platform</h3>
        {data.platforms.length === 0 ? (
          <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>
            Connect an ad platform in the <strong>Connections</strong> tab, then click <strong>Sync now</strong>.
          </div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr>{['Platform','Spend','Leads','Booked','CPL','CPA','ROAS'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>{data.platforms.map(p => (
              <tr key={p.platform}>
                <td style={styles.td}><Badge c={PLATFORM_COLOR[p.platform]||G.purple} bg={(PLATFORM_COLOR[p.platform]||G.purple)+'22'}>{PLATFORM_LABEL[p.platform]||p.platform}</Badge></td>
                <td style={styles.td}>{fmtUsd(p.spend)}</td>
                <td style={styles.td}>{p.leads}</td>
                <td style={styles.td}>{p.booked} ({p.bookRate}%)</td>
                <td style={styles.td}>{fmtUsd(p.cpl)}</td>
                <td style={styles.td}>{fmtUsd(p.cpa)}</td>
                <td style={{ ...styles.td, fontWeight:700, color: p.roas>=2?G.green:p.roas>=1?G.yellow:G.red }}>{p.roas}×</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin:'0 0 14px 0', fontSize:16 }}>Top Campaigns by Spend</h3>
        {data.campaigns.length === 0 ? (
          <div style={{ padding:14, color:G.textMuted, fontSize:13, textAlign:'center' }}>No campaign data yet.</div>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr>{['Name','Platform','Spend','Leads','CPL','ROAS'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
            <tbody>{data.campaigns.slice(0,10).map(c => (
              <tr key={c.id}>
                <td style={{ ...styles.td, fontWeight:600 }}>{c.name}</td>
                <td style={styles.td}><Badge c={PLATFORM_COLOR[c.platform]||G.purple} bg={(PLATFORM_COLOR[c.platform]||G.purple)+'22'}>{PLATFORM_LABEL[c.platform]||c.platform}</Badge></td>
                <td style={styles.td}>{fmtUsd(c.spendToDate)}</td>
                <td style={styles.td}>{c.leads}</td>
                <td style={styles.td}>{fmtUsd(c.cpl)}</td>
                <td style={{ ...styles.td, fontWeight:700, color: c.roas>=2?G.green:c.roas>=1?G.yellow:G.red }}>{c.roas}×</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  </div>;
}

// ─── Connections tab: connect/disconnect ad platforms + pick ad account ──────
function ConnectionsTab({ toast }) {
  const [conns, setConns] = useState({});
  const [picker, setPicker] = useState(null); // { provider, accounts }
  const [creative, setCreative] = useState({ active:false, rates:{} });
  const [usage, setUsage] = useState({ monthToDate:{ totalUsd:0, byKind:[] }, rates:{} });
  const load = async () => {
    try {
      const r = await api('/api/integrations/connections');
      setConns(r);
    } catch(e) { /* ignore */ }
    try { const r = await api('/api/marketing/creative/status'); setCreative(r); } catch {}
    try { const r = await api('/api/marketing/usage'); setUsage(r); } catch {}
  };
  useEffect(() => { load(); }, []);

  const connect = async (provider) => {
    try {
      const { url } = await api(`/api/integrations/${provider}/connect`);
      window.location.href = url;
    } catch(e) { toast(e.message,'error'); }
  };
  const disconnect = async (provider) => {
    if (!confirm(`Disconnect ${PROVIDER_LABEL[provider]||provider}?`)) return;
    try { await api(`/api/integrations/${provider}/disconnect`, { method:'POST' }); load(); toast('Disconnected'); }
    catch(e) { toast(e.message,'error'); }
  };
  const sync = async (provider) => {
    toast(`Syncing ${PROVIDER_LABEL[provider]||provider}...`);
    try {
      const r = await api(`/api/integrations/${provider}/sync`, { method:'POST' });
      if (r.error) toast(r.error, 'error');
      else toast(`✅ ${PROVIDER_LABEL[provider]||provider}: ${r.campaigns||r.leads||0} synced`);
    } catch(e) { toast(e.message,'error'); }
  };
  const pickMetaAccount = async () => {
    try {
      const r = await api('/api/marketing/meta/ad-accounts');
      setPicker({ provider:'meta_ads', accounts: r.accounts, kind:'meta' });
    } catch(e) { toast(e.message,'error'); }
  };
  const pickTikTokAdv = async () => {
    try {
      const r = await api('/api/marketing/tiktok/advertisers');
      setPicker({ provider:'tiktok_ads', accounts: r.advertisers, kind:'tiktok' });
    } catch(e) { toast(e.message,'error'); }
  };
  const promptGoogleAdsCustomerId = async () => {
    const id = prompt('Enter your Google Ads Customer ID (XXX-XXX-XXXX from the top-right of ads.google.com):');
    if (!id) return;
    try { await api('/api/marketing/google-ads/select-customer', { method:'POST', body:{ customerId: id }}); toast('✅ Google Ads customer saved'); load(); }
    catch(e) { toast(e.message,'error'); }
  };
  const [diagnose, setDiagnose] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const runDiagnose = async () => {
    setDiagnosing(true);
    try { const r = await api('/api/marketing/google-ads/diagnose'); setDiagnose(r); }
    catch(e) { toast(e.message, 'error'); }
    finally { setDiagnosing(false); }
  };

  const ad = (provider) => {
    const c = conns[provider] || {};
    const isConnected = c.connected;
    const meta = PROVIDER_META[provider];
    return (
      <div key={provider} style={{ ...styles.card, borderColor: isConnected ? meta.color+'66' : G.cardBorder }}>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:42, height:42, borderRadius:10, background:meta.color+'22', color:meta.color, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22, flexShrink:0 }}>{meta.icon}</div>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <strong style={{ fontSize:15 }}>{meta.label}</strong>
              {isConnected ? <Badge c={G.green} bg={G.greenLight}>Connected</Badge> : <Badge c={G.textMuted} bg={G.input}>Not connected</Badge>}
            </div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{meta.desc}</div>
          </div>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
          {!isConnected && <Btn sz="sm" v="primary" onClick={()=>connect(provider)}>Connect</Btn>}
          {isConnected && provider==='meta_ads' && <Btn sz="sm" onClick={pickMetaAccount}>Pick Ad Account</Btn>}
          {isConnected && provider==='tiktok_ads' && <Btn sz="sm" onClick={pickTikTokAdv}>Pick Advertiser</Btn>}
          {isConnected && provider==='google_ads' && <Btn sz="sm" onClick={promptGoogleAdsCustomerId}>Set Customer ID</Btn>}
          {(provider==='google_ads' || provider==='google_lsa') && <Btn sz="sm" onClick={runDiagnose} disabled={diagnosing}>{diagnosing ? '🩺…' : '🩺 Diagnose'}</Btn>}
          {isConnected && <Btn sz="sm" v="cyan" onClick={()=>sync(provider)}>🔄 Sync</Btn>}
          {isConnected && <Btn sz="sm" v="danger" onClick={()=>disconnect(provider)}>Disconnect</Btn>}
        </div>
      </div>
    );
  };

  return <div>
    <div style={{ marginBottom:14, color:G.textMuted, fontSize:13 }}>
      Connect your ad accounts to pull spend, leads, and ROAS into the unified Reports view. After connecting Meta or TikTok, click <strong>Pick Ad Account</strong> to select which ad account / advertiser to sync.
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(320px,1fr))', gap:14, marginBottom:18 }}>
      {['meta_ads','google_ads','google_lsa','tiktok_ads'].map(ad)}
    </div>

    {diagnose && <div style={{ ...styles.card, padding:16, marginBottom:18, borderColor: diagnose.ok ? G.green+'66' : G.red+'66' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
        <strong style={{ fontSize:14 }}>🩺 Google Ads / LSA Diagnostic — {diagnose.ok ? '✅ All clear' : `⚠ ${diagnose.blockedCount} blocker${diagnose.blockedCount===1?'':'s'}`}</strong>
        <Btn sz="xs" v="ghost" onClick={()=>setDiagnose(null)}>Close</Btn>
      </div>
      {diagnose.steps.map((s, i) => (
        <div key={i} style={{ padding:'8px 10px', borderRadius:6, marginBottom:6,
          background: s.status==='block' ? G.red+'22' : s.status==='ok' ? G.green+'14' : G.input,
          borderLeft: `3px solid ${s.status==='block' ? G.red : s.status==='ok' ? G.green : G.yellow}` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', gap:8 }}>
            <strong style={{ fontSize:13 }}>{s.status==='ok'?'✅':s.status==='block'?'🚫':'⏳'} {s.title}</strong>
            <span style={{ fontSize:11, color:G.textMuted }}>{s.detail}</span>
          </div>
          {s.fix && <div style={{ fontSize:12, color: s.status==='block'?G.text:G.textMuted, marginTop:4, lineHeight:1.4 }}>
            → {s.fix}
          </div>}
        </div>
      ))}
      <div style={{ display:'flex', gap:10, fontSize:11, marginTop:6 }}>
        <a href={diagnose.quickLinks.cloudConsole} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>Cloud Console ↗</a>
        <a href={diagnose.quickLinks.apiCenter} target="_blank" rel="noreferrer" style={{ color:G.cyan }}>API Center ↗</a>
      </div>
    </div>}

    {/* AI Creative Studio card — provider-agnostic copy */}
    <div style={{ ...styles.card, borderColor: creative.active ? G.purple+'66' : G.cardBorder }}>
      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
        <div style={{ width:42, height:42, borderRadius:10, background:G.purple+'22', color:G.purple, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>🎨</div>
        <div style={{ flex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <strong style={{ fontSize:15 }}>AI Ad Creative Studio</strong>
            {creative.active
              ? <Badge c={G.green} bg={G.greenLight}>Active</Badge>
              : <Badge c={G.textMuted} bg={G.input}>Coming soon</Badge>}
          </div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
            Generate scroll-stopping image and video ads in seconds. Charged per generation on your monthly invoice.
          </div>
          {creative.active && creative.rates && (
            <div style={{ marginTop:10, display:'flex', gap:18, flexWrap:'wrap', fontSize:12, color:G.text }}>
              <span><strong>Standard image</strong> · {fmtUsd((creative.rates.standardImage||0)/100)}</span>
              <span><strong>Premium image</strong> · {fmtUsd((creative.rates.premiumImage||0)/100)}</span>
              <span><strong>Animated clip</strong> · {fmtUsd((creative.rates.videoFromImage||0)/100)}</span>
              <span><strong>Full video</strong> · {fmtUsd((creative.rates.videoFromPrompt||0)/100)}</span>
            </div>
          )}
        </div>
      </div>
      {creative.active && usage.monthToDate.totalUsd > 0 && (
        <div style={{ marginTop:12, padding:10, background:G.input, borderRadius:8, fontSize:12 }}>
          <strong>Month to date:</strong> {fmtUsd(usage.monthToDate.totalUsd)} ·{' '}
          {usage.monthToDate.byKind.map((k,i) => (
            <span key={k.kind}>{i>0 && ' · '}{k.n} {k.kind}{k.n>1?'s':''}</span>
          ))}
        </div>
      )}
    </div>

    {/* Picker modal */}
    {picker && <Modal open onClose={()=>setPicker(null)} width={520}>
      <h2 style={{ margin:'0 0 14px 0' }}>Pick {picker.kind==='meta'?'Meta Ad Account':'TikTok Advertiser'}</h2>
      <div style={{ maxHeight:380, overflow:'auto' }}>
        {picker.accounts.length === 0 ? (
          <div style={{ padding:18, color:G.textMuted, textAlign:'center' }}>No ad accounts found on this profile.</div>
        ) : picker.accounts.map(a => {
          const id = a.id || a.advertiser_id;
          const name = a.name || a.advertiser_name || id;
          return <div key={id}
            onClick={async () => {
              try {
                if (picker.kind==='meta') await api('/api/marketing/meta/select-account', { method:'POST', body:{ adAccountId: id }});
                else await api('/api/marketing/tiktok/select-advertiser', { method:'POST', body:{ advertiserId: id }});
                toast('✅ Saved');
                setPicker(null);
                load();
              } catch(e) { toast(e.message,'error'); }
            }}
            style={{ padding:12, borderBottom:`1px solid ${G.cardBorder}`, cursor:'pointer' }}>
            <strong>{name}</strong>
            <div style={{ fontSize:12, color:G.textMuted }}>{id}{a.currency?` · ${a.currency}`:''}</div>
          </div>;
        })}
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
        <Btn onClick={()=>setPicker(null)}>Close</Btn>
      </div>
    </Modal>}
  </div>;
}

const PROVIDER_LABEL = { meta_ads:'Meta Ads', google_ads:'Google Ads', google_lsa:'Google LSA', tiktok_ads:'TikTok Ads' };
const PROVIDER_META = {
  meta_ads:   { label:'Meta Ads',           desc:'Facebook + Instagram. Pulls campaign spend, leads, and ROAS.', color:'#1877f2', icon:'📘' },
  google_ads: { label:'Google Ads',         desc:'Search + display. Requires developer token + customer ID.',   color:'#34a853', icon:'🔍' },
  google_lsa: { label:'Google Local Services Ads', desc:'Per-lead billing. Auto-imports each LSA lead as a contact.', color:'#4285f4', icon:'📍' },
  tiktok_ads: { label:'TikTok Ads',         desc:'TikTok For Business. Pulls advertiser-level spend & conversions.', color:'#000', icon:'🎵' },
};

function CampaignModal({ c, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(c||{}); }, [c]);
  if (!c) return null;
  const isNew = !f.id;
  const save = async () => {
    if (!f.name || !f.platform) return alert('Name and platform required');
    try {
      if (isNew) await api('/api/marketing/campaigns', { method:'POST', body: f });
      else await api(`/api/marketing/campaigns/${f.id}`, { method:'PATCH', body: f });
      onSaved();
    } catch(e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 14px 0' }}>{isNew?'New Campaign':f.name}</h2>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Name *" style={{ gridColumn:'1/-1' }}><Input value={f.name||''} onChange={e=>setF({...f,name:e.target.value})}/></Field>
      <Field label="Platform *">
        <Select value={f.platform||'google_lsa'} onChange={e=>setF({...f,platform:e.target.value})}>
          {PLATFORMS.map(p => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
        </Select>
      </Field>
      <Field label="Status">
        <Select value={f.status||'active'} onChange={e=>setF({...f,status:e.target.value})}>
          <option>active</option><option>paused</option><option>ended</option>
        </Select>
      </Field>
      <Field label="Monthly Budget"><Input type="number" value={f.budgetMonthly||0} onChange={e=>setF({...f,budgetMonthly:Number(e.target.value)})}/></Field>
      <Field label="Spend to Date"><Input type="number" value={f.spendToDate||0} onChange={e=>setF({...f,spendToDate:Number(e.target.value)})}/></Field>
      <Field label="Leads Generated"><Input type="number" value={f.leads||0} onChange={e=>setF({...f,leads:Number(e.target.value)})}/></Field>
      <Field label="Jobs Booked"><Input type="number" value={f.bookedJobs||0} onChange={e=>setF({...f,bookedJobs:Number(e.target.value)})}/></Field>
      <Field label="Revenue Attributed" style={{ gridColumn:'1/-1' }}><Input type="number" value={f.revenueAttributed||0} onChange={e=>setF({...f,revenueAttributed:Number(e.target.value)})}/></Field>
    </div>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
  </Modal>;
}

function PreviewModal({ asset, onClose, onSaved, onApprove, toast }) {
  const [a, setA] = useState(null);
  useEffect(() => { if (asset) setA({ ...asset }); }, [asset]);
  if (!asset) return null;
  const save = async () => {
    try { await api(`/api/marketing/assets/${asset.id}`, { method:'PATCH', body: { title: a.title, copy: a.copy, imageUrl: a.imageUrl, videoUrl: a.videoUrl }}); onSaved(); }
    catch(e) { toast(e.message,'error'); }
  };

  return <Modal open onClose={onClose} width={680}>
    <h2 style={{ margin:'0 0 8px 0' }}>👁️ Preview & Edit</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>Refine the copy, then push live to your ad platform.</div>

    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <div>
        <Field label="Title (internal)"><Input value={a?.title||''} onChange={e=>setA({...a, title:e.target.value})}/></Field>
        <Field label="Ad copy"><Textarea value={a?.copy||''} onChange={e=>setA({...a, copy:e.target.value})} style={{ minHeight:140 }}/></Field>
        {a?.type==='image' && <Field label="Image URL (optional)"><Input value={a?.imageUrl||''} onChange={e=>setA({...a, imageUrl:e.target.value})}/></Field>}
        {a?.type==='video' && <Field label="Video URL (optional)"><Input value={a?.videoUrl||''} onChange={e=>setA({...a, videoUrl:e.target.value})}/></Field>}
      </div>
      <div>
        <div style={{ fontSize:11, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Live Preview</div>
        <div style={{ ...styles.card, padding:0, overflow:'hidden', background:'#fff', color:'#1a1a2e' }}>
          {a?.imageUrl && <img src={a.imageUrl} alt="" style={{ width:'100%', display:'block' }}/>}
          {!a?.imageUrl && a?.type==='image' && <div style={{ aspectRatio:'1.91/1', display:'flex', alignItems:'center', justifyContent:'center', background:'#e5e7eb', color:'#6b7280', fontSize:13 }}>Image preview</div>}
          {a?.type==='video' && <div style={{ aspectRatio:'9/16', display:'flex', alignItems:'center', justifyContent:'center', background:'#000', color:'#fff', fontSize:13 }}>▶ Video</div>}
          <div style={{ padding:12 }}>
            <div style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>Sponsored · {asset.platform||asset.campaignName}</div>
            <div style={{ fontWeight:700, fontSize:15, marginTop:4 }}>{a?.title||'(headline)'}</div>
            <div style={{ fontSize:13, marginTop:6, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{a?.copy||'(copy)'}</div>
            <button style={{ marginTop:10, background:'#1877f2', color:'#fff', border:'none', padding:'8px 14px', borderRadius:6, fontWeight:700, fontSize:13, cursor:'pointer' }}>Get Free Estimate</button>
          </div>
        </div>
      </div>
    </div>

    <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <div style={{ display:'flex', gap:10 }}>
        <Btn onClick={save}>💾 Save edits</Btn>
        <Btn v="green" onClick={onApprove}>✓ Push Live</Btn>
      </div>
    </div>
  </Modal>;
}

// ─── Multi-channel social post modal ───────────────────────────────────────
function SocialPostModal({ asset, onClose, onDone, toast }) {
  const [status, setStatus] = useState(null);
  const [selected, setSelected] = useState({});
  const [customCaption, setCustomCaption] = useState('');
  const [posting, setPosting] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    if (!asset) return;
    setResults(null);
    api('/api/social/status').then(setStatus).catch(()=>setStatus({}));
    setCustomCaption('');
    setSelected({});
  }, [asset]);

  if (!asset) return null;
  const platforms = [
    { key:'facebook_page',     label:'📘 Facebook',  available: status?.facebook?.connected },
    { key:'instagram_business',label:'📷 Instagram', available: status?.instagram?.connected },
    { key:'youtube',           label:'▶️ YouTube',   available: status?.youtube?.connected, requiresVideo: true },
    { key:'tiktok_organic',    label:'🎵 TikTok',    available: status?.tiktok?.connected, requiresVideo: true },
    { key:'gbp',               label:'📍 Google Biz',available: status?.gbp?.connected },
  ];

  const toggle = (k) => setSelected(s => ({ ...s, [k]: !s[k] }));
  const selectedKeys = Object.keys(selected).filter(k => selected[k]);

  const post = async () => {
    if (!selectedKeys.length) return toast('Pick at least one platform','error');
    setPosting(true);
    try {
      const r = await api(`/api/marketing/assets/${asset.id}/post-to-socials`, {
        method:'POST',
        body:{ platforms: selectedKeys, customCaption: customCaption.trim() || undefined },
      });
      setResults(r.results);
      const okCount = r.results.filter(x=>x.ok).length;
      const failCount = r.results.length - okCount;
      if (failCount === 0) toast(`✅ Posted to ${okCount} channel${okCount>1?'s':''}`);
      else if (okCount === 0) toast(`All failed — see details`,'error');
      else toast(`Posted to ${okCount}, ${failCount} failed`,'error');
    } catch (e) { toast(e.message,'error'); }
    finally { setPosting(false); }
  };

  return <Modal open onClose={onClose} width={620}>
    <h2 style={{ margin:'0 0 10px' }}>📲 Post to Socials</h2>
    <div style={{ color:G.textMuted, fontSize:13, marginBottom:14 }}>
      Pick channels — Claude writes a tailored caption for each, then posts.
    </div>

    {/* Preview */}
    <div style={{ display:'flex', gap:14, padding:14, background:G.input, borderRadius:10, marginBottom:14 }}>
      {asset.video_url ? (
        <video src={asset.video_url} muted loop style={{ width:120, height:120, objectFit:'cover', borderRadius:8, background:'#000' }} onMouseEnter={e=>e.target.play()} onMouseLeave={e=>e.target.pause()}/>
      ) : asset.image_url && (
        <img src={asset.image_url} alt="" style={{ width:120, height:120, objectFit:'cover', borderRadius:8 }}/>
      )}
      <div style={{ flex:1, minWidth:0 }}>
        <strong style={{ fontSize:14 }}>{asset.title}</strong>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4, lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:3, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{asset.copy}</div>
      </div>
    </div>

    {/* Platform picker */}
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:12, color:G.textMuted, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em', marginBottom:8 }}>Channels</div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))', gap:8 }}>
        {platforms.map(p => {
          const disabled = !p.available || (p.requiresVideo && !asset.video_url);
          const isSelected = !!selected[p.key];
          return (
            <div key={p.key}
              onClick={()=>!disabled && toggle(p.key)}
              style={{
                padding:'10px 12px', borderRadius:8, cursor: disabled ? 'not-allowed' : 'pointer',
                background: isSelected ? G.purpleDim : G.input,
                border: `2px solid ${isSelected ? G.purple : 'transparent'}`,
                opacity: disabled ? 0.4 : 1,
              }}>
              <div style={{ fontSize:14, fontWeight:600 }}>{p.label}</div>
              <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>
                {!p.available ? 'Not connected' : p.requiresVideo && !asset.video_url ? 'Needs video' : isSelected ? '✓ Selected' : 'Tap to select'}
              </div>
            </div>
          );
        })}
      </div>
    </div>

    {/* Custom caption (optional) */}
    <Field label="Caption override (leave blank = AI writes one per channel)">
      <Textarea value={customCaption} onChange={e=>setCustomCaption(e.target.value)} placeholder="Auto-generate from ad copy + platform style…" style={{ minHeight:80 }}/>
    </Field>

    {/* Results */}
    {results && (
      <div style={{ marginTop:14, padding:12, background:G.input, borderRadius:10 }}>
        <strong style={{ fontSize:12, color:G.textMuted, textTransform:'uppercase' }}>Results</strong>
        <div style={{ marginTop:8 }}>
          {results.map((r, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', fontSize:13 }}>
              <span>{r.platform}</span>
              <span style={{ color: r.ok ? G.green : G.red, fontWeight:600 }}>
                {r.ok ? '✓ Posted' : '✗ ' + (r.error||'failed').slice(0,40)}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}

    <div style={{ display:'flex', justifyContent:'space-between', marginTop:18 }}>
      <Btn onClick={onClose}>{results ? 'Close' : 'Cancel'}</Btn>
      {!results && <Btn v="primary" onClick={post} disabled={posting || !selectedKeys.length}>
        {posting ? <Spinner sz={14}/> : `📲 Post to ${selectedKeys.length || 0} channel${selectedKeys.length===1?'':'s'}`}
      </Btn>}
      {results && <Btn v="primary" onClick={onDone}>Done</Btn>}
    </div>
  </Modal>;
}

function Stat({ label, value, sub, color }) {
  return <div style={{ ...styles.card, borderColor:color+'66' }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:6 }}>{value}</div>
    <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{sub}</div>
  </div>;
}
